import type { Contact, OrgTableRow } from '@/types';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import type { ICellRendererParams } from 'ag-grid-enterprise';
import { Copy } from 'lucide-react';
import React, { type FC, memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { copyContactToClipboard } from './ContactCell.utils';

type ContactCellProps = ICellRendererParams<OrgTableRow, Contact | null | undefined>;

const ContactCellComponent: FC<ContactCellProps> = ({ value }) => {
  const { t: tGeneral } = useTranslation('general');
  const data = value;

  const handleCopyContact = useCallback(() => {
    if (!data) return;
    void copyContactToClipboard(data, tGeneral('copy_contact_success'), tGeneral('copy_contact_error'));
  }, [data, tGeneral]);

  if (!data) {
    return (
      <Stack alignItems="center" fullHeight fullWidth justifyContent="center">
        <Text size="sm">—</Text>
      </Stack>
    );
  }

  const firstName = data.first_name || '';
  const lastName = data.last_name || '';
  const fullName = `${firstName} ${lastName}`.trim() || '—';

  const hasContactInfo = data.email || data.phone;

  const tooltipContent = (
    <Stack alignItems="start" gap={1} orientation="vertical" fullWidth>
      <Stack alignItems="center" fullWidth justifyContent="space-between" orientation="horizontal">
        <Text as="div" size="sm" weight="semibold">
          {fullName}
        </Text>
        <Tooltip
          content={tGeneral('copy_contact_details')}
          placement="top"
          trigger={
            <Button
              aria-label={tGeneral('copy_contact_details')}
              data-testid="copy-contact-btn"
              onPress={handleCopyContact}
              size="sm"
              visual="unstyled"
            >
              <Copy size={14} />
            </Button>
          }
        />
      </Stack>
      {data.email && (
        <a href={`mailto:${data.email}`} style={{ textDecoration: 'none' }}>
          <Text as="div" size="xs" color="muted">
            {data.email}
          </Text>
        </a>
      )}
      {data.phone && (
        <Text as="div" size="xs" color="muted">
          {data.phone}
        </Text>
      )}
    </Stack>
  );

  const nameText = <Text size="sm">{fullName}</Text>;

  return (
    <Stack alignItems="center" fullHeight fullWidth justifyContent="center">
      {hasContactInfo ? (
        <Tooltip
          content={tooltipContent}
          placement="bottom"
          trigger={
            <Button data-testid="contact-trigger-btn" visual="unstyled">
              {nameText}
            </Button>
          }
        />
      ) : (
        nameText
      )}
    </Stack>
  );
};

export const ContactCell = memo(ContactCellComponent);
